import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  selectCollectionSummaries,
  reorderCollections,
  useT,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";
import { Folder, FolderPlus } from "react-feather";
import { CollectionForm } from "../components/CollectionForm";
import { SortableList, GripHandle } from "../components/SortableList";
import { StorageImage } from "../components/StorageImage";

export default function Collections() {
  const t = useT();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const collections = useAppSelector(selectCollectionSummaries);
  const [showForm, setShowForm] = useState(false);
  const [reordering, setReordering] = useState(false);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-extrabold text-ink">{t("nav.collections")}</h1>
        {collections.length > 1 && (
          <button
            onClick={() => setReordering((r) => !r)}
            className={
              reordering
                ? "rounded-lg bg-terracotta hover:bg-terracotta-dark transition-colors px-4 py-1.5 text-sm font-bold text-white cursor-pointer"
                : "rounded-lg border border-line hover:bg-surface-2 transition-colors px-3 py-1.5 text-sm font-semibold text-ink-soft cursor-pointer"
            }
          >
            {reordering ? t("common.done") : t("collections.reorderShort")}
          </button>
        )}
      </div>

      {reordering ? (
        <SortableList
          items={collections}
          onReorder={(items) =>
            dispatch(reorderCollections({ collectionIds: items.map((i) => i.id) }))
          }
          className="space-y-2 max-w-2xl"
          renderItem={(c, handle) => (
            <div className="flex items-center gap-2 bg-surface border border-line rounded-xl pl-1 pr-3">
              <GripHandle handle={handle} className="w-6 h-16 text-ink-faint" />
              <StorageImage
                stored={c.resolvedImage}
                imgClassName="w-14 h-14 rounded-lg object-cover bg-surface-2 shrink-0"
                fallback={
                  <div className="w-14 h-14 rounded-lg bg-surface-2 flex items-center justify-center shrink-0">
                    <Folder className="w-5 h-5 text-ink-faint" />
                  </div>
                }
              />
              <div className="min-w-0 py-2">
                <p className="font-semibold text-ink truncate">{c.name}</p>
                <p className="text-sm text-ink-soft">
                  {t("library.recipeCount", { count: c.recipeCount })}
                </p>
              </div>
            </div>
          )}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {collections.map((c) => (
            <Link key={c.id} to={`/collection/${c.id}`} className="block group">
              <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-surface-2 relative">
                <StorageImage
                  stored={c.resolvedImage}
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <Folder className="w-8 h-8 text-ink-faint" />
                    </div>
                  }
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-bold text-sm truncate">{c.name}</p>
                  {c.description && (
                    <p className="text-white/80 text-xs line-clamp-2">{c.description}</p>
                  )}
                  <p className="text-white/80 text-xs">
                    {t("library.recipeCount", { count: c.recipeCount })}
                  </p>
                </div>
              </div>
            </Link>
          ))}

          <button
            onClick={() => setShowForm(true)}
            className="rounded-2xl border-2 border-dashed border-line aspect-[4/3] flex flex-col items-center justify-center px-4 hover:border-terracotta transition-colors cursor-pointer"
          >
            {/* Match the app: terracotta circle icon, bold terracotta CTA line,
                muted hint underneath. */}
            <span className="w-11 h-11 rounded-full bg-terracotta/10 flex items-center justify-center mb-2">
              <FolderPlus className="w-5 h-5 text-terracotta" />
            </span>
            <span className="text-base font-bold text-terracotta text-center">
              {t("collections.newCollection")}
            </span>
            <span className="text-xs text-ink-faint text-center mt-0.5">
              {t("collections.createHint")}
            </span>
          </button>
        </div>
      )}

      {showForm && (
        <CollectionForm
          onClose={() => setShowForm(false)}
          onCreated={(id) => navigate(`/collection/${id}`)}
        />
      )}
    </div>
  );
}
